<template>
    <div>
        <Nav></Nav>
        <b-container fluid="xl">
            <b-row>
                <b-col>
                    <h1>Copiador de Menús Uber</h1>
                    <p class="text-muted">Selecciona un menú origen y los menús destino para realizar la copia</p>
                </b-col>
            </b-row>

            <!-- Selección de Menú Origen -->
            <b-row class="mt-4">
                <b-col>
                    <b-card>
                        <template #header>
                            <h4 class="mb-0">
                                <i class="fas fa-file-export"></i> Menú Origen
                            </h4>
                        </template>

                        <b-form-group label="Selecciona el menú que se copiará:" label-for="origin-menu">
                            <b-form-select id="origin-menu" v-model="selectedOriginMenu" :options="menuOptions"
                                placeholder="Seleccionar menú origen..." @change="onOriginMenuChange">
                                <template #first>
                                    <b-form-select-option :value="null" disabled>
                                        -- Selecciona un menú origen --
                                    </b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>

                        <div v-if="selectedOriginMenu" class="mt-3">
                            <b-alert show variant="info">
                                <strong>Menú seleccionado:</strong> {{ getMenuName(selectedOriginMenu) }}
                            </b-alert>
                        </div>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Modo de copia -->
            <b-row class="mt-4" v-if="selectedOriginMenu">
                <b-col>
                    <b-card>
                        <template #header>
                            <h4 class="mb-0"><i class="fas fa-sliders-h"></i> ¿Qué copiar?</h4>
                        </template>
                        <b-form-radio-group v-model="copyMode" @change="onCopyModeChange" stacked>
                            <b-form-radio value="full"><strong>Menú completo</strong>
                                <b-form-text class="text-muted">Copia todos los productos, categorías y modificadores del menú origen.</b-form-text>
                            </b-form-radio>
                            <b-form-radio value="categories" class="mt-2"><strong>Ciertas categorías</strong>
                                <b-form-text class="text-muted">Elige categorías completas; se copian la categoría, sus productos y sus modificadores a los menús destino.</b-form-text>
                            </b-form-radio>
                            <b-form-radio value="products" class="mt-2"><strong>Solo ciertos productos</strong>
                                <b-form-text class="text-muted">Elige productos específicos; se agregan a los menús destino junto con sus modificadores.</b-form-text>
                            </b-form-radio>
                        </b-form-radio-group>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Selección de categorías (solo en modo 'categories') -->
            <b-row class="mt-4" v-if="selectedOriginMenu && copyMode === 'categories'">
                <b-col>
                    <b-card>
                        <template #header>
                            <div class="d-flex justify-content-between align-items-center">
                                <h4 class="mb-0"><i class="fas fa-layer-group"></i> Categorías a copiar</h4>
                                <b-badge variant="primary" pill>{{ selectedCategoryIds.length }} seleccionada(s)</b-badge>
                            </div>
                        </template>

                        <div v-if="categoriesLoading" class="text-center p-4">
                            <b-spinner class="mr-2"></b-spinner> Cargando categorías del menú origen...
                        </div>
                        <template v-else>
                            <b-row class="mb-2">
                                <b-col md="8">
                                    <b-form-input v-model="categorySearch" placeholder="Buscar categoría..." size="sm"></b-form-input>
                                </b-col>
                                <b-col md="4" class="text-right">
                                    <b-form-checkbox v-model="allFilteredCategoriesSelected" @change="toggleSelectAllCategories" class="d-inline-block">
                                        Seleccionar todas
                                    </b-form-checkbox>
                                </b-col>
                            </b-row>
                            <div class="destination-menus-container">
                                <div v-for="cat in filteredCategories" :key="cat.id" class="mb-2 ml-1">
                                    <b-form-checkbox v-model="selectedCategoryIds" :value="cat.id" class="destination-menu-checkbox">
                                        {{ categoryTitle(cat) }}
                                        <small class="text-muted">— {{ (cat.entities && cat.entities.length) || 0 }} producto(s)</small>
                                    </b-form-checkbox>
                                </div>
                                <p v-if="filteredCategories.length === 0" class="text-muted mb-0">No hay categorías que coincidan.</p>
                            </div>
                        </template>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Selección de productos (solo en modo 'products') -->
            <b-row class="mt-4" v-if="selectedOriginMenu && copyMode === 'products'">
                <b-col>
                    <b-card>
                        <template #header>
                            <div class="d-flex justify-content-between align-items-center">
                                <h4 class="mb-0"><i class="fas fa-list"></i> Productos a copiar</h4>
                                <b-badge variant="primary" pill>{{ selectedItemIds.length }} seleccionado(s)</b-badge>
                            </div>
                        </template>

                        <div v-if="itemsLoading" class="text-center p-4">
                            <b-spinner class="mr-2"></b-spinner> Cargando productos del menú origen...
                        </div>
                        <template v-else>
                            <b-row class="mb-2">
                                <b-col md="8">
                                    <b-form-input v-model="itemSearch" placeholder="Buscar producto..." size="sm"></b-form-input>
                                </b-col>
                                <b-col md="4" class="text-right">
                                    <b-form-checkbox v-model="allFilteredSelected" @change="toggleSelectAll" class="d-inline-block">
                                        Seleccionar todos
                                    </b-form-checkbox>
                                </b-col>
                            </b-row>
                            <div class="destination-menus-container">
                                <div v-for="item in filteredItems" :key="item.id" class="mb-2 ml-1">
                                    <b-form-checkbox v-model="selectedItemIds" :value="item.id" class="destination-menu-checkbox">
                                        {{ itemTitle(item) }}
                                        <small class="text-muted">— ${{ itemPrice(item) }}</small>
                                    </b-form-checkbox>
                                </div>
                                <p v-if="filteredItems.length === 0" class="text-muted mb-0">No hay productos que coincidan.</p>
                            </div>
                        </template>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Selección de Menús Destino -->
            <b-row class="mt-4" v-if="selectedOriginMenu">
                <b-col>
                    <b-card>
                        <template #header>
                            <h4 class="mb-0">
                                <i class="fas fa-file-import"></i> Menús Destino
                            </h4>
                        </template>

                        <b-form-group label="Selecciona los menús donde se copiará el contenido:">
                            <div class="destination-menus-container">
                                <div v-for="menu in availableDestinationMenus" :key="menu.value" class="mb-2 ml-3">
                                    <b-form-checkbox v-model="selectedDestinationMenus" :value="menu.value"
                                        class="destination-menu-checkbox">
                                        <strong>{{ menu.text }}</strong>
                                    </b-form-checkbox>
                                </div>
                            </div>
                        </b-form-group>

                        <div v-if="selectedDestinationMenus.length > 0" class="mt-3">
                            <b-alert show variant="warning">
                                <i class="fas fa-exclamation-triangle"></i>
                                <strong>{{ selectedDestinationMenus.length }}</strong> menú(s) seleccionado(s) para
                                copia.
                                <br>
                                <small v-if="copyMode === 'full'">El contenido de estos menús será sobreescrito con el del menú origen.</small>
                                <small v-else-if="copyMode === 'categories'">Las categorías seleccionadas (con sus productos y modificadores) se agregarán a estos menús.</small>
                                <small v-else>Los productos seleccionados se agregarán/actualizarán en estos menús.</small>
                            </b-alert>
                        </div>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Botón de Acción -->
            <b-row class="mt-4" v-if="selectedOriginMenu && selectedDestinationMenus.length > 0">
                <b-col>
                    <b-card>
                        <b-button variant="primary" size="lg" block @click="showConfirmModal" :disabled="copying || !canCopy">
                            <i class="fas fa-copy"></i>
                            <span v-if="copyMode === 'full'">Copiar Menú completo a {{ selectedDestinationMenus.length }} destino(s)</span>
                            <span v-else-if="copyMode === 'categories'">Copiar {{ selectedCategoryIds.length }} categoría(s) a {{ selectedDestinationMenus.length }} destino(s)</span>
                            <span v-else>Copiar {{ selectedItemIds.length }} producto(s) a {{ selectedDestinationMenus.length }} destino(s)</span>
                        </b-button>
                        <p v-if="copyMode === 'products' && selectedItemIds.length === 0" class="text-muted text-center mt-2 mb-0">
                            <small>Selecciona al menos un producto para copiar.</small>
                        </p>
                        <p v-if="copyMode === 'categories' && selectedCategoryIds.length === 0" class="text-muted text-center mt-2 mb-0">
                            <small>Selecciona al menos una categoría para copiar.</small>
                        </p>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Progreso de Copia -->
            <b-row class="mt-4" v-if="copying">
                <b-col>
                    <b-card>
                        <template #header>
                            <h4 class="mb-0">
                                <i class="fas fa-spinner fa-spin"></i> Progreso de Copia
                            </h4>
                        </template>

                        <b-progress :value="copyProgress" :max="selectedDestinationMenus.length" show-progress animated
                            variant="primary" class="mb-3"></b-progress>

                        <div class="copy-status">
                            <p><strong>{{ copyProgress }}</strong> de <strong>{{ selectedDestinationMenus.length
                                    }}</strong> menús
                                copiados</p>
                            <div v-if="currentCopyingMenu" class="text-muted">
                                <small>Copiando a: {{ getMenuName(currentCopyingMenu) }}...</small>
                            </div>
                        </div>

                        <!-- Log de operaciones -->
                        <div v-if="copyLog.length > 0" class="mt-3">
                            <h6>Registro de operaciones:</h6>
                            <div class="copy-log">
                                <div v-for="(log, index) in copyLog" :key="index" :class="['log-entry', log.type]">
                                    <i :class="log.icon"></i>
                                    {{ log.message }}
                                </div>
                            </div>
                        </div>
                    </b-card>
                </b-col>
            </b-row>

        </b-container>

        <!-- Modal de Confirmación -->
        <b-modal id="confirm-copy-modal" v-model="showModal" title="Confirmar Copia de Menú" @ok="startCopy"
            @cancel="showModal = false" ok-title="Sí, Copiar" cancel-title="Cancelar" ok-variant="danger">
            <div class="text-center">
                <i class="fas fa-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
                <h5 class="mt-3">⚠️ Confirmar copia</h5>
                <p v-if="copyMode === 'full'" class="text-danger">
                    <strong>El contenido de los menús destino será sobreescrito con el del menú origen.</strong>
                </p>
                <p v-else-if="copyMode === 'categories'" class="text-info">
                    <strong>Se copiarán {{ selectedCategoryIds.length }} categoría(s) (con sus productos y modificadores) a los menús destino.</strong>
                </p>
                <p v-else class="text-info">
                    <strong>Se copiarán {{ selectedItemIds.length }} producto(s) (con sus modificadores) a los menús destino.</strong>
                </p>
                <hr>
                <div class="text-left">
                    <p><strong>Menú Origen:</strong> {{ getMenuName(selectedOriginMenu) }}</p>
                    <p><strong>Menús Destino ({{ selectedDestinationMenus.length }}):</strong></p>
                    <ul class="pl-4">
                        <li v-for="menuId in selectedDestinationMenus" :key="menuId">
                            {{ getMenuName(menuId) }}
                        </li>
                    </ul>
                </div>
                <p class="mt-3">
                    <strong>¿Estás seguro de que deseas continuar?</strong>
                </p>
            </div>
        </b-modal>

    </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import axios from "axios";

export default {
    name: "UberMenuCopy",
    components: { Nav },
    data() {
        return {
            // Datos de menús
            allMenus: [],
            selectedOriginMenu: null,
            selectedDestinationMenus: [],

            // Modo de copia: 'full' (menú completo), 'categories' (ciertas categorías) o
            // 'products' (solo ciertos productos)
            copyMode: 'full',
            originItems: [],
            itemsLoading: false,
            selectedItemIds: [],
            itemSearch: '',

            // Selección por categorías
            originCategories: [],
            categoriesLoading: false,
            selectedCategoryIds: [],
            categorySearch: '',

            // Estados de UI
            loading: false,
            copying: false,
            showModal: false,

            // Progreso de copia
            copyProgress: 0,
            currentCopyingMenu: null,
            copyLog: [],

            // Control de errores
            hasErrors: false
        };
    },
    computed: {
        menuOptions() {
            return this.allMenus.map(menu => ({
                value: menu.uuid,
                text: menu.name
            }));
        },

        availableDestinationMenus() {
            return this.allMenus
                .filter(menu => menu.uuid !== this.selectedOriginMenu)
                .map(menu => ({
                    value: menu.uuid,
                    text: menu.name
                }));
        },

        filteredItems() {
            const q = this.itemSearch.trim().toLowerCase();
            if (!q) return this.originItems;
            return this.originItems.filter(it => this.itemTitle(it).toLowerCase().includes(q));
        },

        allFilteredSelected: {
            get() {
                return this.filteredItems.length > 0 &&
                    this.filteredItems.every(it => this.selectedItemIds.includes(it.id));
            },
            set() { /* handled by toggleSelectAll */ }
        },

        filteredCategories() {
            const q = this.categorySearch.trim().toLowerCase();
            if (!q) return this.originCategories;
            return this.originCategories.filter(c => this.categoryTitle(c).toLowerCase().includes(q));
        },

        allFilteredCategoriesSelected: {
            get() {
                return this.filteredCategories.length > 0 &&
                    this.filteredCategories.every(c => this.selectedCategoryIds.includes(c.id));
            },
            set() { /* handled by toggleSelectAllCategories */ }
        },

        canCopy() {
            if (this.copyMode === 'products') {
                return this.selectedItemIds.length > 0 && this.selectedDestinationMenus.length > 0;
            }
            if (this.copyMode === 'categories') {
                return this.selectedCategoryIds.length > 0 && this.selectedDestinationMenus.length > 0;
            }
            return this.selectedDestinationMenus.length > 0;
        }
    },
    mounted() {
        this.loadMenus();
    },
    methods: {
        async loadMenus() {
            this.loading = true;
            try {
                const response = await axios.get('/uber/mastermenus');
                this.allMenus = response.data;
                this.makeToast('Éxito', 'Menús cargados correctamente', 'success');
            } catch (error) {
                this.makeToast('Error', 'Error al cargar los menús: ' + (error.response?.data || error.message), 'danger');
            } finally {
                this.loading = false;
            }
        },

        onOriginMenuChange() {
            // Limpiar selecciones de destino, productos y categorías si cambia el origen
            this.selectedDestinationMenus = [];
            this.copyLog = [];
            this.copyProgress = 0;
            this.originItems = [];
            this.selectedItemIds = [];
            this.itemSearch = '';
            this.originCategories = [];
            this.selectedCategoryIds = [];
            this.categorySearch = '';
            if (this.copyMode === 'products') {
                this.loadOriginItems();
            } else if (this.copyMode === 'categories') {
                this.loadOriginCategories();
            }
        },

        onCopyModeChange(mode) {
            if (mode === 'products' && this.selectedOriginMenu && this.originItems.length === 0) {
                this.loadOriginItems();
            } else if (mode === 'categories' && this.selectedOriginMenu && this.originCategories.length === 0) {
                this.loadOriginCategories();
            }
        },

        async loadOriginItems() {
            if (!this.selectedOriginMenu) return;
            this.itemsLoading = true;
            try {
                const response = await axios.get(`/uber/mastermenu/${this.selectedOriginMenu}/items`);
                this.originItems = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                this.makeToast('Error', 'No se pudieron cargar los productos del menú origen', 'danger');
                this.originItems = [];
            } finally {
                this.itemsLoading = false;
            }
        },

        async loadOriginCategories() {
            if (!this.selectedOriginMenu) return;
            this.categoriesLoading = true;
            try {
                const response = await axios.get(`/uber/mastermenu/${this.selectedOriginMenu}/categories`);
                this.originCategories = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                this.makeToast('Error', 'No se pudieron cargar las categorías del menú origen', 'danger');
                this.originCategories = [];
            } finally {
                this.categoriesLoading = false;
            }
        },

        itemTitle(item) {
            return (item && item.title && item.title.translations && item.title.translations.en) || item.id || '(sin nombre)';
        },

        categoryTitle(cat) {
            return (cat && cat.title && cat.title.translations && cat.title.translations.en) || cat.id || '(sin nombre)';
        },

        toggleSelectAllCategories(checked) {
            const filteredIds = this.filteredCategories.map(c => c.id);
            if (checked) {
                const set = new Set(this.selectedCategoryIds);
                filteredIds.forEach(id => set.add(id));
                this.selectedCategoryIds = Array.from(set);
            } else {
                this.selectedCategoryIds = this.selectedCategoryIds.filter(id => !filteredIds.includes(id));
            }
        },

        itemPrice(item) {
            const cents = (item && item.price_info && item.price_info.price) || 0;
            return (cents / 100).toFixed(2);
        },

        toggleSelectAll(checked) {
            const filteredIds = this.filteredItems.map(it => it.id);
            if (checked) {
                const set = new Set(this.selectedItemIds);
                filteredIds.forEach(id => set.add(id));
                this.selectedItemIds = Array.from(set);
            } else {
                this.selectedItemIds = this.selectedItemIds.filter(id => !filteredIds.includes(id));
            }
        },

        getMenuName(menuUuid) {
            const menu = this.allMenus.find(m => m.uuid === menuUuid);
            return menu ? menu.name : 'Menú desconocido';
        },

        showConfirmModal() {
            this.showModal = true;
        },

        async startCopy() {
            this.copying = true;
            this.copyProgress = 0;
            this.copyLog = [];
            this.hasErrors = false;

            this.addLog('Iniciando proceso de copia...', 'info');

            for (let i = 0; i < this.selectedDestinationMenus.length; i++) {
                const destinationMenuId = this.selectedDestinationMenus[i];
                this.currentCopyingMenu = destinationMenuId;

                try {
                    this.addLog(`Copiando a ${this.getMenuName(destinationMenuId)}...`, 'info');
                    
                    await this.copyMenuToDestination(this.selectedOriginMenu, destinationMenuId);

                    this.copyProgress++;
                    this.addLog(`✅ Copia exitosa a ${this.getMenuName(destinationMenuId)}`, 'success');

                    // Mostrar toast temporal de progreso
                    this.makeToast(
                        'Progreso',
                        `Menú copiado a ${this.getMenuName(destinationMenuId)} (${this.copyProgress}/${this.selectedDestinationMenus.length})`,
                        'info',
                        3000
                    );

                } catch (error) {
                    this.hasErrors = true;
                    this.addLog(`❌ Error al copiar a ${this.getMenuName(destinationMenuId)}: ${error.message}`, 'error');
                    this.makeToast('Error', `Error al copiar a ${this.getMenuName(destinationMenuId)}`, 'danger');
                }
            }

            this.currentCopyingMenu = null;
            this.copying = false;

            // Mostrar resultado final
            if (this.hasErrors) {
                this.makeToast(
                    'Copia Completada con Errores',
                    `Se completó la copia con algunos errores. Revisa el registro para más detalles.`,
                    'warning',
                    8000
                );
            } else {
                this.makeToast(
                    'Copia Completada',
                    `Se copiaron exitosamente ${this.copyProgress} menús desde ${this.getMenuName(this.selectedOriginMenu)}`,
                    'success',
                    6000
                );
            }

            this.addLog(`Proceso finalizado. Total copiados: ${this.copyProgress}/${this.selectedDestinationMenus.length}`, 'info');
        },

        async copyMenuToDestination(originMenuUuid, destinationMenuUuid) {
            const url = `/uber/mastermenu/${originMenuUuid}/copyTo/${destinationMenuUuid}`;
            // 'products': send selected item ids -> the backend copies those products (+ modifier
            //   groups, sub-products, combos).
            // 'categories': send selected category ids -> the backend copies the categories, their
            //   products and modifiers, and adds the categories to the destination menu layout.
            // 'full': no body -> whole-menu copy.
            let payload;
            if (this.copyMode === 'products') {
                payload = { item_ids: this.selectedItemIds };
            } else if (this.copyMode === 'categories') {
                payload = { category_ids: this.selectedCategoryIds };
            }
            const response = await axios.post(url, payload);
            return response.data;
        },

        addLog(message, type = 'info') {
            const iconMap = {
                info: 'fas fa-info-circle text-info',
                success: 'fas fa-check-circle text-success',
                error: 'fas fa-times-circle text-danger',
                warning: 'fas fa-exclamation-triangle text-warning'
            };

            this.copyLog.push({
                message,
                type,
                icon: iconMap[type] || iconMap.info,
                timestamp: new Date().toLocaleTimeString()
            });
        },

        makeToast(title, content, variant, autoHideDelay = 5000) {
            this.$bvToast.toast(content, {
                title,
                variant,
                autoHideDelay,
                appendToast: true,
                toaster: 'b-toaster-top-right'
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.destination-menus-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #f8f9fa;
}

.destination-menu-checkbox {
    padding: 0.5rem;
    border-radius: 0.25rem;
    transition: background-color 0.2s;

    &:hover {
        background-color: #e9ecef;
    }
}

.copy-log {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 0.75rem;
    background-color: #f8f9fa;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
}

.log-entry {
    margin-bottom: 0.25rem;
    padding: 0.25rem;
    border-radius: 0.125rem;

    &.success {
        background-color: #d4edda;
        border-left: 3px solid #28a745;
    }

    &.error {
        background-color: #f8d7da;
        border-left: 3px solid #dc3545;
    }

    &.warning {
        background-color: #fff3cd;
        border-left: 3px solid #ffc107;
    }

    &.info {
        background-color: #d1ecf1;
        border-left: 3px solid #17a2b8;
    }
}

.copy-status {
    text-align: center;
}

.card-header h4 {
    color: #495057;
}

// Animaciones
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

// Responsive adjustments
@media (max-width: 768px) {
    .destination-menus-container {
        max-height: 200px;
    }

    .copy-log {
        max-height: 150px;
        font-size: 0.75rem;
    }
}
</style>